import { Injectable } from '@angular/core';
import type { StyleSpecification } from 'maplibre-gl';

/** Per-deployment base tile vendor identifier, set via environment.mapTileProvider */
export type MapTileProvider =
  | 'openstreetmap'
  | 'stamen'
  | 'usgs-satellite'
  | 'maptiler'
  | 'maptiler-satellite';

/**
 * Configuration for MapLibre base tile source
 * Can be set per deployment/environment
 */
export interface MapTileSourceConfig {
  type: 'raster' | 'vector' | 'geojson';
  url?: string; // TileJSON URL or geojson URL
  tiles?: string[]; // Direct tile URLs for raster sources
  attribution?: string;
  tileSize?: number;
  minzoom?: number;
  maxzoom?: number;
  scheme?: 'xyz' | 'tms';
}

export type MapViewMode = 'map' | 'satellite';

/**
 * Service to manage MapLibre tile source configuration
 * Allows per-deployment customization of base map tiles
 */
@Injectable({
  providedIn: 'root',
})
export class MapTileConfigService {
  /**
   * Default tile source: OpenStreetMap Raster tiles
   * Override via setTileSource() for custom configurations
   */
  private tileSource: MapTileSourceConfig = {
    type: 'raster',
    tiles: [
      'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
      'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
      'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
    ],
    tileSize: 256,
    attribution:
      '© OpenStreetMap contributors, © MapTiler, © OpenStreetMap Mapnik',
    minzoom: 0,
    maxzoom: 19,
  };

  /**
   * Set custom tile source configuration
   */
  setTileSource(config: MapTileSourceConfig): void {
    this.tileSource = { ...this.tileSource, ...config };
  }

  /**
   * Get current tile source configuration
   */
  getTileSource(): MapTileSourceConfig {
    return { ...this.tileSource };
  }

  /**
   * Set OpenStreetMap raster tiles
   */
  setOpenStreetMapTiles(): void {
    this.setTileSource({
      type: 'raster',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
      minzoom: 0,
      maxzoom: 19,
    });
  }

  /**
   * Set Stamen Terrain tiles
   */
  setStamenTerrainTiles(): void {
    this.setTileSource({
      type: 'raster',
      tiles: [
        'https://a.tile.stamen.com/terrain/{z}/{x}/{y}.png',
        'https://b.tile.stamen.com/terrain/{z}/{x}/{y}.png',
        'https://c.tile.stamen.com/terrain/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© Stamen Design, © OpenStreetMap contributors',
      minzoom: 0,
      maxzoom: 18,
    });
  }

  /**
   * Set Satellite imagery (USGS)
   */
  setUSGSSatelliteTiles(): void {
    this.setTileSource({
      type: 'raster',
      tiles: [
        'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: '© USGS',
      minzoom: 0,
      maxzoom: 16,
    });
  }

  /**
   * Set MapTiler vector tiles (requires API key)
   */
  setMapTilerVectorTiles(apiKey: string, style: string = 'basic'): void {
    this.setTileSource({
      type: 'vector',
      url: `https://api.maptiler.com/maps/${style}/tilejson.json?key=${apiKey}`,
      attribution: '© MapTiler © OpenStreetMap contributors',
    });
  }

  /** Apply the tile source for a deployment's configured provider (environment.mapTileProvider) */
  applyProvider(provider: MapTileProvider | undefined, mapTilerApiKey?: string): void {
    this.setTileSource(this.getTileSourceForProvider(provider, mapTilerApiKey));
  }

  /** Resolve tile source without mutating service state */
  getTileSourceForProvider(
    provider: MapTileProvider | undefined,
    mapTilerApiKey?: string,
  ): MapTileSourceConfig {
    switch (provider) {
      case 'usgs-satellite':
        return {
          type: 'raster',
          tiles: [
            'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}',
          ],
          tileSize: 256,
          attribution: '© USGS',
          minzoom: 0,
          maxzoom: 16,
        };
      case 'stamen':
        return {
          type: 'raster',
          tiles: [
            'https://a.tile.stamen.com/terrain/{z}/{x}/{y}.png',
            'https://b.tile.stamen.com/terrain/{z}/{x}/{y}.png',
            'https://c.tile.stamen.com/terrain/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution: '© Stamen Design, © OpenStreetMap contributors',
          minzoom: 0,
          maxzoom: 18,
        };
      case 'maptiler-satellite':
        /* Global satellite imagery, unlike `usgs-satellite` which is United States only.
           Without a key MapTiler answers 403 for every tile, so fall through to the
           US-only source rather than render an empty map. */
        if (mapTilerApiKey) {
          return {
            type: 'raster',
            tiles: [
              `https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${mapTilerApiKey}`,
            ],
            /* satellite-v2 serves 512px tiles, like the MapTiler streets style. Declaring 256
               here makes MapLibre pick the wrong zoom level and overscale the imagery. */
            tileSize: 512,
            /* Aerial imagery, not OpenStreetMap-derived, so it does not carry the OSM credit
               the streets style does. */
            attribution: '© MapTiler',
            minzoom: 0,
            maxzoom: 20,
          };
        }
        return this.getTileSourceForProvider('usgs-satellite');
      case 'maptiler':
        if (mapTilerApiKey) {
          return {
            type: 'raster',
            tiles: [
              `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${mapTilerApiKey}`,
            ],
            tileSize: 512,
            attribution: '© MapTiler © OpenStreetMap contributors',
            minzoom: 0,
            maxzoom: 19,
          };
        }
        return {
          type: 'raster',
          tiles: [
            'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors',
          minzoom: 0,
          maxzoom: 19,
        };
      default:
        return {
          type: 'raster',
          tiles: [
            'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors',
          minzoom: 0,
          maxzoom: 19,
        };
    }
  }

  /** Build a MapLibre style from the currently configured tile source (no hardcoded third-party style host) */
  buildRasterStyle(): StyleSpecification {
    const tileConfig = this.getTileSource();
    return {
      version: 8,
      sources: {
        'base-tiles': {
          type: 'raster',
          tiles: tileConfig.tiles,
          tileSize: tileConfig.tileSize || 256,
          attribution: tileConfig.attribution,
          minzoom: tileConfig.minzoom || 0,
          maxzoom: tileConfig.maxzoom || 19,
          scheme: tileConfig.scheme || 'xyz',
        },
      },
      layers: [
        {
          id: 'background',
          type: 'background',
          paint: {
            'background-color': '#f0f0f0',
          },
        },
        {
          id: 'base-tiles-layer',
          type: 'raster',
          source: 'base-tiles',
          paint: {
            'raster-opacity': 1,
          },
        },
      ],
    } as StyleSpecification;
  }

  /** Build MapLibre style with both map and satellite base layers for runtime toggle */
  buildDualRasterStyle(
    mapConfig: MapTileSourceConfig,
    satelliteConfig: MapTileSourceConfig,
    activeView: MapViewMode,
  ): StyleSpecification {
    return {
      version: 8,
      sources: {
        'map-base-source': {
          type: 'raster',
          tiles: mapConfig.tiles,
          tileSize: mapConfig.tileSize || 256,
          attribution: mapConfig.attribution,
          minzoom: mapConfig.minzoom || 0,
          maxzoom: mapConfig.maxzoom || 19,
          scheme: mapConfig.scheme || 'xyz',
        },
        'satellite-base-source': {
          type: 'raster',
          tiles: satelliteConfig.tiles,
          tileSize: satelliteConfig.tileSize || 256,
          attribution: satelliteConfig.attribution,
          minzoom: satelliteConfig.minzoom || 0,
          maxzoom: satelliteConfig.maxzoom || 19,
          scheme: satelliteConfig.scheme || 'xyz',
        },
      },
      layers: [
        {
          id: 'background',
          type: 'background',
          paint: {
            'background-color': '#f0f0f0',
          },
        },
        {
          id: 'map-base-layer',
          type: 'raster',
          source: 'map-base-source',
          layout: {
            visibility: activeView === 'map' ? 'visible' : 'none',
          },
          paint: {
            'raster-opacity': 1,
          },
        },
        {
          id: 'satellite-base-layer',
          type: 'raster',
          source: 'satellite-base-source',
          layout: {
            visibility: activeView === 'satellite' ? 'visible' : 'none',
          },
          paint: {
            'raster-opacity': 1,
          },
        },
      ],
    } as StyleSpecification;
  }
}